import React, { useEffect } from 'react';
import SEO from './SEO';

// Slug a question for an anchor id (used for deep-links from texts/email).
const slugify = (s) =>
  s.toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);

// Single source of truth: each entry is rendered as a <details> AND as a JSON-LD
// FAQPage entry, so the schema and the visible content can never drift.
const FAQ_SECTIONS = [
  {
    title: 'Pricing & Plans',
    items: [
      {
        q: 'How much does Bins Storage cost?',
        a: (
          <>
            Storage starts at <strong>$30/month for a 5x5 unit</strong> and{' '}
            <strong>$50/month for a 5x10 unit</strong>, with larger 10x10 sizes
            available on request. We also offer flexible pricing — if you end up
            using less space than you reserved, we'll automatically downgrade you
            so you only pay for what you actually use.
          </>
        ),
        plain:
          "Storage starts at $30/month for a 5x5 unit and $50/month for a 5x10 unit, with larger 10x10 sizes available on request. We also offer flexible pricing — if you end up using less space than you reserved, we'll automatically downgrade you so you only pay for what you actually use.",
      },
      {
        q: 'What does pickup or delivery cost?',
        a: (
          <>
            Pickup and delivery share the same standard rate, based on your unit
            size: <strong>$60 + $1/min</strong> for a 5x5 unit, and{' '}
            <strong>$120 + $2/min</strong> for a 5x10 or 10x10 unit. We always
            quote you the exact cost upfront when you pick a date, so there's
            never a surprise.
          </>
        ),
        plain:
          "Pickup and delivery share the same standard rate, based on your unit size: $60 + $1/min for a 5x5 unit, and $120 + $2/min for a 5x10 or 10x10 unit. We always quote you the exact cost upfront when you pick a date, so there's never a surprise.",
      },
      {
        q: "Do I have to pay for a full month even if I only store for a few weeks?",
        a: (
          <>
            No — we prorate. You only pay for the time your items are actually in
            storage.
          </>
        ),
        plain:
          'No — we prorate. You only pay for the time your items are actually in storage.',
      },
      {
        q: 'Are there any hidden fees?',
        a: (
          <>
            No. The standard cost is monthly storage plus a pickup/delivery fee
            based on your unit size. Both are shown upfront before you confirm.
          </>
        ),
        plain:
          'No. The standard cost is monthly storage plus a pickup/delivery fee based on your unit size. Both are shown upfront before you confirm.',
      },
    ],
  },
  {
    title: 'What You Can Store',
    items: [
      {
        q: 'What can I store with Bins Storage?',
        a: (
          <>
            Common items customers store with us include boxes, suitcases, bags,
            furniture, appliances (mini fridges, microwaves), bikes, and dorm
            room items. There are some things we can't accept, including:
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Explosives, weapons, or ammunition</li>
              <li>Flammable or hazardous materials</li>
              <li>Perishable food or live animals</li>
              <li>Stolen property or items whose possession is unlawful</li>
            </ul>
            <p style={{ marginTop: 10, marginBottom: 0 }}>
              If you're not sure whether something's allowed,{' '}
              <a href="/Contact" style={{ color: '#AAB5E0' }}>just ask</a> before
              pickup.
            </p>
          </>
        ),
        plain:
          "Common items customers store with us include boxes, suitcases, bags, furniture, appliances (mini fridges, microwaves), bikes, and dorm room items. There are some things we can't accept, including: explosives, weapons, or ammunition; flammable or hazardous materials; perishable food or live animals; and stolen property or items whose possession is unlawful. If you're not sure whether something's allowed, just ask before pickup.",
      },
      {
        q: 'Do you provide boxes?',
        a: (
          <>
            Not currently. You can use any cardboard moving boxes (Amazon, Home
            Depot), suitcases, duffel bags, or storage bins — whatever you have
            on hand works.
          </>
        ),
        plain:
          'Not currently. You can use any cardboard moving boxes (Amazon, Home Depot), suitcases, duffel bags, or storage bins — whatever you have on hand works.',
      },
      {
        q: 'Do I need to pack everything myself?',
        a: (
          <>
            Yes — please pack and seal up all loose items before pickup. You don't
            need to disassemble small furniture in advance, but please take apart
            anything large that needs it (like a bed frame). Movers handle the
            furniture and the carrying; we don't pack loose items for you.
          </>
        ),
        plain:
          "Yes — please pack and seal up all loose items before pickup. You don't need to disassemble small furniture in advance, but please take apart anything large that needs it (like a bed frame). Movers handle the furniture and the carrying; we don't pack loose items for you.",
      },
      {
        q: 'Will the movers come up to my apartment or dorm?',
        a: (
          <>
            Yes. As long as there's an elevator (or it's a ground floor), the
            movers will come up, carry your packed items down, and load them into
            the truck. You don't need to bring anything to the curb yourself.
          </>
        ),
        plain:
          "Yes. As long as there's an elevator (or it's a ground floor), the movers will come up, carry your packed items down, and load them into the truck. You don't need to bring anything to the curb yourself.",
      },
    ],
  },
  {
    title: 'Booking & Scheduling',
    items: [
      {
        q: 'How do I sign up?',
        a: (
          <>
            Head to{' '}
            <a href="/Download" style={{ color: '#AAB5E0' }}>
              bins-storage.com/signup
            </a>
            , pick your move-in date and time, and you're set. You'll get a
            confirmation, and we'll text you the day before to confirm details.
          </>
        ),
        plain:
          "Head to bins-storage.com/signup, pick your move-in date and time, and you're set. You'll get a confirmation, and we'll text you the day before to confirm details.",
      },
      {
        q: "What if my preferred time isn't available?",
        a: (
          <>
            <a href="/Contact" style={{ color: '#AAB5E0' }}>Text us</a> — we can
            often manually accommodate slots that aren't showing on the calendar.
            Just go ahead and book any available time, then send us a quick note
            about what you actually want.
          </>
        ),
        plain:
          "Text us — we can often manually accommodate slots that aren't showing on the calendar. Just go ahead and book any available time, then send us a quick note about what you actually want.",
      },
      {
        q: 'Can I change my pickup date, unit size, or box count after booking?',
        a: (
          <>
            Yes — just text us with the change. The app doesn't yet let you
            self-edit those details, but we'll handle it on our end and confirm
            it back.
          </>
        ),
        plain:
          "Yes — just text us with the change. The app doesn't yet let you self-edit those details, but we'll handle it on our end and confirm it back.",
      },
      {
        q: 'What if I need to cancel?',
        a: (
          <>
            No problem — just let us know and we'll cancel the order. No
            cancellation fees.
          </>
        ),
        plain:
          "No problem — just let us know and we'll cancel the order. No cancellation fees.",
      },
    ],
  },
  {
    title: 'Pickup & Delivery Logistics',
    items: [
      {
        q: 'Where do you serve?',
        a: (
          <>
            We serve the <strong>East Bay</strong> (Berkeley, Oakland, Richmond),{' '}
            <strong>San Francisco</strong>, and surrounding areas. We also serve
            the <strong>Tacoma, WA</strong> area (Lakewood, University Place,
            Puyallup). Our Bay Area facility is at 555 Parr Blvd, Richmond, CA
            94801.
          </>
        ),
        plain:
          'We serve the East Bay (Berkeley, Oakland, Richmond), San Francisco, and surrounding areas. We also serve the Tacoma, WA area (Lakewood, University Place, Puyallup). Our Bay Area facility is at 555 Parr Blvd, Richmond, CA 94801.',
      },
      {
        q: 'Can I share a storage unit with a roommate or friend?',
        a: (
          <>
            Absolutely. You can split a unit with anyone you'd like. Just keep
            in mind each pickup or delivery is its own trip and priced at the
            standard rate for your unit size.
          </>
        ),
        plain:
          "Absolutely. You can split a unit with anyone you'd like. Just keep in mind each pickup or delivery is its own trip and priced at the standard rate for your unit size.",
      },
      {
        q: 'Can I drop off or pick up items myself at the facility?',
        a: (
          <>
            We're a fully valet storage service, so customers don't typically
            access the facility. If you have a special situation,{' '}
            <a href="/Contact" style={{ color: '#AAB5E0' }}>reach out</a> and
            we'll do our best to accommodate.
          </>
        ),
        plain:
          "We're a fully valet storage service, so customers don't typically access the facility. If you have a special situation, reach out and we'll do our best to accommodate.",
      },
      {
        q: 'How will I know when the movers arrive?',
        a: (
          <>
            Our movers will text or call you directly the day of your move with
            their ETA. If you need an earlier check-in, just text us.
          </>
        ),
        plain:
          'Our movers will text or call you directly the day of your move with their ETA. If you need an earlier check-in, just text us.',
      },
      {
        q: 'How do I get my stuff back?',
        a: (
          <>
            Request a delivery in the app or text us your preferred date and
            time. Delivery uses the same standard rate as pickup —{' '}
            <strong>$60 + $1/min</strong> for a 5x5 unit, or{' '}
            <strong>$120 + $2/min</strong> for a 5x10 or 10x10 unit. We track
            every box and item with a barcode and photo, so if you only need
            certain items back, you can request just those.
          </>
        ),
        plain:
          'Request a delivery in the app or text us your preferred date and time. Delivery uses the same standard rate as pickup — $60 + $1/min for a 5x5 unit, or $120 + $2/min for a 5x10 or 10x10 unit. We track every box and item with a barcode and photo, so if you only need certain items back, you can request just those.',
      },
    ],
  },
  {
    title: 'Storage Details',
    items: [
      {
        q: 'Are your storage units climate controlled?',
        a: (
          <>
            <strong>No, our units are not climate controlled.</strong> The
            facility is secure and well-ventilated, but if you're storing
            temperature-sensitive items (electronics, vinyl, instruments, etc.),
            reach out and we can help you pack them safely.
          </>
        ),
        plain:
          "No, our units are not climate controlled. The facility is secure and well-ventilated, but if you're storing temperature-sensitive items (electronics, vinyl, instruments, etc.), reach out and we can help you pack them safely.",
      },
      {
        q: 'Do you offer insurance?',
        a: (
          <>
            We don't offer insurance directly, but we can connect you with
            third-party providers if you'd like coverage. Many homeowners or
            renters insurance policies also cover stored items — worth checking
            your existing policy.
          </>
        ),
        plain:
          "We don't offer insurance directly, but we can connect you with third-party providers if you'd like coverage. Many homeowners or renters insurance policies also cover stored items — worth checking your existing policy.",
      },
      {
        q: 'How does item tracking work?',
        a: (
          <>
            When we pick up your items, every box and individual piece gets a
            barcode label. You can see everything you've stored with us in your
            account, with photos. When it's time to get items back, you can
            request all of them or just specific ones.
          </>
        ),
        plain:
          "When we pick up your items, every box and individual piece gets a barcode label. You can see everything you've stored with us in your account, with photos. When it's time to get items back, you can request all of them or just specific ones.",
      },
    ],
  },
  {
    title: 'Why Valet Storage',
    items: [
      {
        q: 'Why valet storage instead of self-storage?',
        a: (
          <>
            Three reasons people choose us over a self-storage unit:
            <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li><strong>No driving back and forth.</strong> We come to you.</li>
              <li><strong>No lifting.</strong> Our movers do the heavy work.</li>
              <li><strong>Visibility.</strong> You can see exactly what you've stored and request specific items back without sorting through a unit.</li>
            </ol>
          </>
        ),
        plain:
          "Three reasons people choose us over a self-storage unit: 1) No driving back and forth — we come to you. 2) No lifting — our movers do the heavy work. 3) Visibility — you can see exactly what you've stored and request specific items back without sorting through a unit.",
      },
    ],
  },
];

// Build JSON-LD from the same data so they stay in lockstep.
const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.plain,
      },
    }))
  ),
};

const pageContainer = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const sectionHeader = {
  color: '#AAB5E0',
  fontSize: 22,
  textAlign: 'center',
  marginTop: 32,
  marginBottom: 8,
  letterSpacing: 0.3,
};

const detailsStyle = {
  borderBottom: '1px solid rgba(170, 181, 224, 0.18)',
  padding: '14px 0',
};

const summaryStyle = {
  color: '#AAB5E0',
  fontSize: 17,
  fontWeight: 500,
  cursor: 'pointer',
  listStyle: 'none',
  outline: 'none',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

const caretStyle = {
  display: 'inline-block',
  transition: 'transform 0.15s ease',
  marginTop: 2,
  width: 14,
  flexShrink: 0,
  color: '#AAB5E0',
  fontSize: 12,
};

const answerStyle = {
  color: '#FFF',
  fontSize: 14,
  marginTop: 10,
  marginLeft: 24,
  opacity: 0.95,
  lineHeight: 1.65,
};

// Inject the bits we can't do via inline style: hide native marker, rotate caret on open.
const localCss = `
  .faq-details summary::-webkit-details-marker { display: none; }
  .faq-details summary::marker { content: ''; }
  .faq-details[open] .faq-caret { transform: rotate(90deg); }
  .faq-details summary:hover { opacity: 0.85; }
`;

const Faq = () => {
  // Open the <details> matching the URL hash on load — so /FAQ#climate-controlled
  // expands the climate-control answer instead of just scrolling past a closed one.
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el && el.tagName === 'DETAILS') {
      el.open = true;
      // Re-scroll after opening so the now-expanded answer is visible
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <>
      <SEO
        title="Bins Storage FAQ | Valet Storage in SF & Tacoma"
        description="Answers to common questions about Bins valet storage in San Francisco, Tacoma, Berkeley, and the Bay Area – including pricing, moving fees, deliveries, and getting started."
        path="/FAQ"
        schema={faqSchema}
      />
      <style>{localCss}</style>
      <div style={pageContainer}>
        <div style={shellCard}>
          <h1 style={{ fontSize: 26, fontWeight: 600, marginTop: 0, marginBottom: 4 }}>FAQ</h1>
          <p style={{ fontSize: 14, opacity: 0.75, marginTop: 0 }}>
            Click any question to expand. Still stuck?{' '}
            <a href="/Contact" style={{ color: '#AAB5E0' }}>Contact us</a>.
          </p>

          {FAQ_SECTIONS.map((section) => (
            <section key={section.title}>
              <div style={sectionHeader}>{section.title}</div>
              {section.items.map((item) => {
                const id = slugify(item.q);
                return (
                  <details key={id} id={id} className="faq-details" style={detailsStyle}>
                    <summary style={summaryStyle}>
                      <span className="faq-caret" style={caretStyle}>▶</span>
                      <span>{item.q}</span>
                    </summary>
                    <div style={answerStyle}>{item.a}</div>
                  </details>
                );
              })}
            </section>
          ))}
        </div>
      </div>
    </>
  );
};

export default Faq;
